'use client'
import { useState, useEffect, useRef } from 'react'
import { sanitizeRichText } from '@/lib/sanitize-rich-text'

/**
 * Renders admin-authored rich-text descriptions (HTML from RichTextEditor)
 * safely on the storefront. Without this, sites rendering `{description}`
 * as plain text show literal "<p>" tags.
 *
 * Clamps to two lines with a Show more toggle by default; pass
 * expandable={false} for non-interactive surfaces (tooltips).
 */
export default function RichDescription({
  html,
  className = 'text-sm text-text-secondary',
  expandable = true,
}: {
  html: string | null | undefined
  className?: string
  expandable?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const [clamped, setClamped] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const clean = sanitizeRichText(html)

  useEffect(() => {
    const el = ref.current
    if (el) setClamped(el.scrollHeight > el.clientHeight + 1)
  }, [clean])

  if (!clean) return null

  return (
    <div>
      <div
        ref={ref}
        className={`${className} prose prose-sm max-w-none [&_p]:my-0.5 [&_img]:max-h-32 [&_img]:rounded-md ${!expanded ? 'line-clamp-2' : ''}`}
        dangerouslySetInnerHTML={{ __html: clean }}
      />
      {expandable && clamped && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setExpanded(prev => !prev) }}
          className="mt-1 text-xs font-medium text-primary hover:text-primary-hover transition-colors"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  )
}
